import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import UtgNav from './components/UtgNav'
import UtgTools from './components/UtgTools.jsx'

import { fetchComponentConfig, updateComponentConfig } from '../../state/actions/componentConfigActions.js'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions.js'
import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { fetchUtg } from '../../state/actions/utgActions.js'

import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal, ccApiInterfaceConfigSignal } from '../../state/signals/componentConfigSignals.js'
import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, componentConfigSchemaErrorSignal, ccSchemaApiInterfaceConfigSignal } from '../../state/signals/componentConfigSchemaSignals'
import { utgSignal, utgLoadedSignal, utgErrorSignal,  } from '../../state/signals/utgSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { checkApiInterfaceConflicts, isEmptyObject, makeFieldsReadonly, scrollToTop } from '../../utils/utils.js'
export default function UtgApiInterfaceDetail() {

    const navigate = useNavigate()
    const { beId, componentId, id } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const [searchParams] = useSearchParams()
    const port = searchParams.get('port')
    const editMode = !!port
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(() => {
        if (beIdChanged) return
        fetchUtg(beId, componentId, config, accessToken).then(
            () => {
                fetchComponentConfigSchema(beId, utgSignal.value.utgVersion, config, accessToken)
                fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
            }
        )

    }, [beId, beIdChanged, componentId, config, accessToken])

    const apiInterfaceSchemaComputed = useComputed(() => {
        if (componentConfigSchemaLoadedSignal.value) {
            // extract the selected api interface schema from the component config schema
            const jsonFormConfigSchema = {
                config: /*componentConfigSchemaSignal.value.config.properties.apiInterfaceConfig?*/ ccSchemaApiInterfaceConfigSignal.value.properties.apiInterface.items.find(a => a.properties.cdType["s4-model-key"] == parseInt(id, 10))
            }
            return jsonFormConfigSchema
        }
        //console.log("component config schema:")
        //console.log(componentConfigSchemaSignal.value)
        return {}
    })

    const apiInterfaceComputed = useComputed(() => {
        if (componentConfigLoadedSignal.value && editMode) {
            console.log("Component config api interface config:")
            console.log(ccApiInterfaceConfigSignal.value)
            const index = ccApiInterfaceConfigSignal.value.apiInterface.findIndex(a => a.listenPort == port)
            const currentApiInterface = ccApiInterfaceConfigSignal.value.apiInterface[index]
            const jsonFormApiInterface = { config: currentApiInterface }
            //console.log("JSON form API interface:")
            //console.log(jsonFormApiInterface)
            return jsonFormApiInterface
        }
        return null
    })
    const apiInterfaceIndexComputed = useComputed(() => {
        if (componentConfigLoadedSignal.value && editMode) {
            // selected apiInterface index. This is used when editing an interface since there are no unique id's
            const index = ccApiInterfaceConfigSignal.value.apiInterface.findIndex(a => a.listenPort == port)
            return index
        }
        return -1
    })
    const pageTitleComputed = useComputed(() => {
        const currentCaption = apiInterfaceSchemaComputed.value?.config?.properties?.cdType['s4-caption'] || ''
        return editMode ?
            `Manage ${currentCaption} UTG API Interface` :
            `Add ${currentCaption} UTG API Interface`
    })

    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmittingSignal.value) {
            throw Error("Form is already being submitted")
            return false
        }
        isFormSubmittingSignal.value = true
        try {
            //console.log('values', values)
            if (values) {

                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: componentConfigSignal.value.config,
                    hash: componentConfigSignal.value.hash,
                    isCommitted: componentConfigSignal.value.isCommitted,
                    lastUpdatedAt: componentConfigSignal.value.lastUpdatedAt,
                    lastUpdatedBy: componentConfigSignal.value.lastUpdatedBy,
                    revisionId: componentConfigSignal.value.revisionId,
                    componentId: componentConfigSignal.value.componentId
                }

                // make sure the apiInterfaceConfig is present before adding to it
                data.config.apiInterfaceConfig = data.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.apiInterfaceConfig.apiInterface[apiInterfaceIndexComputed.value] = values.config
                }
                else {
                    // insert values into the apiInterface array
                    data.config.apiInterfaceConfig.apiInterface.push(values.config)
                }

                // validate listenPort uniqueness in case jsonform validation fails
                if (checkApiInterfaceConflicts(data.config.apiInterfaceConfig.apiInterface)) {
                    addNotification("Unable to save due to conflict. All API Interfaces must have a unique Port.", NotificationTypes.ERROR)
                    scrollToTop("main")
                    return false
                }
                //console.log("All API interfacers have unique ports")
                //console.log(data)
                updateComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, data, config, accessToken)
                    .then(() => {
                        navigate(`/be/${beId}/utgs/${componentId}/apiinterfaces`)
                        addNotification('Api Interface was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Api Interface.")
                        scrollToTop("main")
                        return false
                    })
                    .finally(() => {
                        isFormSubmittingSignal.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }
        finally {
            //console.log("Inside handleFormSubmit finally block")
            isFormSubmittingSignal.value = false
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        }
        else {
            navigate('../apiinterfaces')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    const jsonFormDataComputed = useComputed(() => {

        if (!componentConfigLoadedSignal.value || !componentConfigSchemaLoadedSignal.value) {
            return
        }

        if (isEmptyObject(componentConfigSchemaSignal.value) || isEmptyObject(componentConfigSignal.value)) {
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(apiInterfaceSchemaComputed.value)
        

        // disable editing of all fields if canEditSignal is false
        if (!userPermissionsSummary?.value?.canEditUtgs) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }
        console.log("Structured clone:")
        console.log(clonedSchema)
        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'CONFIG'
        //console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        const formData = {
            schema: clonedSchema,
            loadedComponentConfig: componentConfigSignal.value.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: userPermissionsSummary?.value?.canEditUtgs
        }
        if (apiInterfaceComputed.value) {
            formData.value = apiInterfaceComputed.value
        }
        //console.log('formData from ApiInterfaceDetail', stringifyRecursive(formData))
        return formData
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const isFormSubmittingSignal = useSignal(false)
    const accordionData = [
        { name: 'API Interface Data', data: apiInterfaceComputed.value },
        { name: 'API Interface Schema', data: apiInterfaceSchemaComputed.value },
        { name: 'API Interface Index', data: apiInterfaceIndexComputed.value },
        { name: 'Component Config', data: componentConfigSignal.value },
        { name: 'Component Schema', data: componentConfigSchemaSignal.value },
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]



    return (
        <>
            <ConfirmationModal show={showCancelModalSignal.value} handleCancel={() => showCancelModalSignal.value = false} handleConfirm={() => navigate('../apiinterfaces')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? <>Manage {utgSignal.value.componentNickname}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={pageTitleComputed.value} canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[componentConfigLoadedSignal]} errorSignals={[componentConfigErrorSignal]}>
                    <UtgTools componentConfig={componentConfigSignal.value} canCreateProfile={userPermissionsSummary?.value?.canEditProfiles} beId={beId} config={config} accessToken={accessToken}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="2"></UtgNav>
            <Loadable loadedSignals={[utgLoadedSignal, componentConfigLoadedSignal, componentConfigSchemaLoadedSignal]} errorSignals={[utgErrorSignal, componentConfigErrorSignal, componentConfigSchemaErrorSignal]}>
                <JsonForm data={jsonFormDataComputed.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}